/**
 * The boundary between what the backend says and what v1.5 draws.
 *
 * Three vocabularies meet here and none of them is the same size:
 *
 * - the **wire** lifecycle, `'loading' | 'writing' | 'updating' | 'complete' | 'accepted' |
 *   'rejected' | 'deleting' | 'deleted'`, which the old widgets spelled three different ways;
 * - v1.5's **`AgentLifecycle`**, eight members drawn from the eight `Agent state` artboards, which has
 *   **no `loading` and no `writing`**;
 *
 * The mapping lives on OUR side on purpose. `API-PARITY.md §1.1` asks for a consolidated
 * `v1_5/Chat/types.ts` that does not exist yet, and adding members to Beka's unions to make a wire
 * value fit would be deciding a design question with a type alias. When that file lands, this one
 * shrinks.
 */
import type { ActivityTool, AgentLifecycle, ItemState } from '~/design-system/components/v1_5/Chat';

/* ── lifecycle ──────────────────────────────────────────────────────────────────────────────── */

/** What the stream calls the state of one agent action. Wider than what the design draws. */
export type WireLifecycle =
  | 'loading'
  | 'writing'
  | 'updating'
  | 'complete'
  | 'accepted'
  | 'rejected'
  | 'failed'
  | 'deleting'
  | 'deleted';

/**
 * Wire → drawn.
 *
 * `loading` and `writing` both land on `running`, and that is not a lossy shortcut: v1.5 already
 * collapses `updating` onto `running`'s mark for the same reason — `LIFECYCLE_STATE` has seven marks
 * for eight states, and the folder calls that "the finding". Something in flight looks like something
 * in flight. What distinguishes writing from loading is the BODY (a skeleton versus a list), which is
 * a different axis and is carried separately.
 */
const LIFECYCLE: Record<WireLifecycle, AgentLifecycle> = {
  loading: 'running',
  writing: 'running',
  updating: 'updating',
  complete: 'complete',
  accepted: 'accepted',
  rejected: 'rejected',
  failed: 'failed',
  deleting: 'deleting',
  deleted: 'deleted',
};

/** Unknown wire values settle on `running` rather than throwing — a new backend state should not blank the row. */
export function toLifecycle(wire: string | undefined): AgentLifecycle {
  return LIFECYCLE[(wire ?? '') as WireLifecycle] ?? 'running';
}

/** Per-step state inside a card. The wire uses more words than the three the list row draws. */
const ITEM_STATE: Record<string, ItemState> = {
  complete: 'done',
  completed: 'done',
  done: 'done',
  viewing: 'running',
  reading: 'running',
  searching: 'running',
  loading: 'running',
  in_progress: 'running',
  running: 'running',
  pending: 'pending',
  // A failed step has no mark of its own in `ActivityListRow` — `ItemState` is three values and the
  // failure is reported by the CARD, not by the row. Resting it at `pending` is the honest read: the
  // thing did not happen.
  failed: 'pending',
};

export function toItemState(wire: string | undefined): ItemState {
  return ITEM_STATE[(wire ?? '').toLowerCase()] ?? 'pending';
}

/* ── tools ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * There is no tool-name mapper here any more, and the reason is worth recording.
 *
 * This file used to carry a 26-entry `PlanningToolName` → `ActivityTool` map. It had no consumer and
 * could not have one: the stream never sends a generic "a tool ran" event carrying a backend tool
 * name. Every tool reaches us as its own event — `linearAction`, `githubAction`, `webSearchCall`,
 * `outlineAction`, `editCanvasDispatch` — so the mark is known at the handler, statically, and a
 * lookup through a string would only add a way to get it wrong.
 *
 * The one field that does carry a raw tool name is `sectionContentDelta.toolName`, and that event is
 * deliberately not drawn (it is partial tool-call JSON arriving many times a second).
 *
 * If a generic tool event ever appears, the map is in git history at `c0689714`.
 *
 * One did, in 2026-09: `event: toolStep`, carrying `tool` as the backend's own name, at `start` and
 * again at `result`. So a map is back, and it is SMALL on purpose: only the tools the stream
 * actually names today. Everything else lands on `clock`, the same mark the legacy `action` row
 * wears, so an unknown tool draws a plain row rather than nothing.
 */
const TOOL: Record<string, ActivityTool> = {
  list_images: 'image',
  add_image: 'image-add',
  place_image: 'image-place',
  remove_image: 'image-remove',
  delete_image: 'image-remove',
  // The generic artifact tools (2026-09-08): read, edit with search-and-replace, look at the result.
  read_artifact: 'artifact',
  edit_artifact: 'canvas-edit',
  look_at_artifact: 'artifact',
  // The rest of the rows (2026-09-14). The backend's ToolStepLabels is the one list and sends the glyph
  // on the wire; this map is the fallback for an event without one.
  create_artifact: 'artifact',
  write_artifact: 'artifact',
  brandKit_read: 'brand-kit',
  brandKit_edit: 'brand-kit',
  brandKit_setup: 'brand-kit',
  createDocument: 'document',
  updateDocument: 'document',
  editDocument: 'document',
  deleteDocument: 'document',
  files_getContext: 'document',
  images_getContext: 'image',
  linear_getSavedContext: 'linear',
  set_point_card: 'point-card-set',
  get_point_card: 'point-card-read',
  stranger_read: 'stranger-read',
  // Still `agent`, and therefore still unmarked once it settles — the cookbook is the one row in
  // this group Beka did not assign a glyph (2026-09-20). It is a gap, not a decision.
  useCookbook: 'agent',
  loadSkill: 'skill',
};

/** The glyphs a row may carry, as the wire may name them; anything else falls back to the tool map. */
const ICONS = new Set<ActivityTool>([
  'artifact', 'document', 'canvas-edit', 'brand-kit', 'outline', 'goal', 'research', 'image',
  'image-add', 'image-place', 'image-remove', 'clock', 'chat', 'agent', 'linear', 'figma', 'notion', 'github',
  // The backend cannot name these yet — `ToolStepLabels` sends `agent` for all four — so today they
  // are reached through the `TOOL` map above. Listed anyway so that the day Guri adds the constants,
  // the wire value is accepted instead of being silently dropped to `clock`.
  'point-card-set', 'point-card-read', 'stranger-read', 'skill', 'thinking',
]);

/**
 * Backend tool name → the mark v1.5 draws, unless the event names the glyph itself. Unknown names
 * settle on `clock`.
 *
 * ## The one exception, and why it is narrow
 *
 * The wire's glyph normally wins, because `ToolStepLabels` is the one list and a backend that knows
 * about a tool we do not should still be able to put the right mark on it. `agent` is the exception:
 * that list predates 2026-09-20, when the point card, the stranger read and the skill loader were
 * each given a mark of their own, and it still sends `agent` for all of them — which draws NOTHING
 * once the row settles, since the dot matrix only exists while it moves.
 *
 * So `agent` off the wire is read as *unspecified* rather than as an answer, for the tools this file
 * has since mapped. It is deliberately not a general "our map wins": any other glyph the backend
 * names still takes precedence, and the day `ToolStepLabels` sends `point-card-set` the wire wins
 * again and this clause stops firing on its own — it is written to expire rather than to be removed.
 *
 * The RELOAD path needs none of this: a persisted row keeps only its sentence, so it comes back
 * through `toolFromLabel`, which was updated in the same pass.
 */
export function toActivityTool(wire: string | undefined, icon?: string | null): ActivityTool {
  const known = TOOL[wire ?? ''];
  const stale = icon === 'agent' && known !== undefined && known !== 'agent';
  if (icon && !stale && ICONS.has(icon as ActivityTool)) return icon as ActivityTool;
  return known ?? 'clock';
}

/**
 * The same map, keyed on WORDING, for the reload path.
 *
 * A finished tool row is persisted as an `ACTION` part carrying only its final label — the tool
 * name does not survive. The labels are the backend's own sentences, so the prefix is the tool:
 * "Checked your library" is `list_images`, "Placed 'hero.png'" is `place_image`. First match wins;
 * a label nothing matches is a `clock` row, exactly as it was before.
 */
const LABEL_TOOL: [RegExp, ActivityTool][] = [
  [/^(Checking|Checked) your library/, 'image'],
  [/^(Adding an image|Added ')/, 'image-add'],
  [/^(Placing|Placed|Replacing|Replaced|Moved|Resized|Styled|Could not place)/, 'image-place'],
  // The document rows come before the generic image removal, or "Removed a document" reads as a picture.
  [/^(Writing|Wrote|Updating|Updated|Editing|Edited|Removing|Removed|Could not \w+) (a|the) document/, 'document'],
  [/^(Removing|Removed|Deleting|Deleted) /, 'image-remove'],
  [/^(Reading|Read|Checking|Checked) Artifact/, 'artifact'],
  [/^(Editing|Edited|Could not edit) Artifact/, 'canvas-edit'],
  [/^(Reading|Read|Checking|Checked) an artifact/, 'artifact'],
  [/^(Editing|Edited|Could not edit) (an|the) artifact/, 'canvas-edit'],
  // The finished wording of ToolStepLabels (backend), 2026-09-14. Same prefixes as its running wording.
  [/^(Creating|Created|Writing|Wrote|Could not (create|write)) (an|the) artifact/, 'artifact'],
  [/^(Reading|Read|Updating|Updated|Setting up|Set up|Could not (read|update|set up)) (the|a) brand kit/, 'brand-kit'],
  [/^(Reading|Read|Could not read) (your files|the saved workspace context|saved workspace context)/, 'document'],
  [/^(Reviewing|Reviewed|Could not read) your images/, 'image'],
  [/^(Reading|Read|Could not read) the point card/, 'point-card-read'],
  [/^(Reading|Read|Could not read) the deck as a stranger/, 'stranger-read'],
  [/^(Saving|Saved|Could not save) the point card/, 'point-card-set'],
  // Split off the cookbook, which keeps `agent`: same sentence shape, different marks now.
  [/^(Loading|Loaded|Could not load) (a skill|the skill)/, 'skill'],
  [/^(Consulting|Consulted|Could not read) (your cookbook|the cookbook)/, 'agent'],
  [/^(Reading|Read|Could not read) (Linear|a Linear|the Linear)/, 'linear'],
  [/^(Searching|Searched|Reading|Read|Could not (search|read)) (Notion|a Notion|the Notion)/, 'notion'],
  [/^(Searching|Searched|Reading|Read|Could not (search|read)) (GitHub|a GitHub|the GitHub)/, 'github'],
  [/^(Reading|Read|Could not read) (a Figma|the Figma|Figma)/, 'figma'],
];

export function toolFromLabel(label: string): ActivityTool {
  return LABEL_TOOL.find(([re]) => re.test(label))?.[1] ?? 'clock';
}

/* ── meta ───────────────────────────────────────────────────────────────────────────────── */

/**
 * Rows that carry NO right-aligned hint, whatever the backend puts there.
 *
 * `look_at_artifact` — **Checked an artifact** — sends one of `contract ok`, `2 rejects` or
 * `contract check pending`, and all three are the artifact contract's own bookkeeping: "contract" is
 * a word from the HTML rulebook the agent writes against, not one the reader has ever been shown
 * (Beka, 2026-09-23: "no need for contract ok … the indication on the right"). Keyed on the ROW
 * rather than on the three strings, so a fourth wording tomorrow is covered too, and so the failure
 * case goes with the rest: when the check fails the row turns to its failed state and lists each
 * reject underneath, so a count beside it only repeats what is already on screen.
 */
const NO_META_TOOLS: ReadonlySet<string> = new Set(['look_at_artifact']);

/**
 * The row's right-aligned hint — minus the revision number, and minus it altogether on the rows in
 * `NO_META_TOOLS`.
 *
 * `rev 1 · 0 images`, `rev 2`. The rev is the artifact's internal version counter, which means
 * nothing to the person reading the thread: it does not name what changed, it cannot be opened, and
 * it is not the number the Directory shows beside the artifact. Beka, 2026-09-20: "rev no need for
 * that." So a meta that LEADS with one is dropped whole, which also takes the `· 0 images` that
 * trails it — a count of nothing is not a hint either.
 *
 * Only metas that lead with `rev`. Every other hint the backend sends is doing real work —
 * `artifact 02`, `3 edits`, `2 images`, `288 × 288` — and is untouched, which is why this is a
 * prefix test rather than a search for `rev` anywhere in the string.
 *
 * Filtered on the way IN rather than hidden in CSS, so the row genuinely has no trailing run: the
 * meta sits in the flex line between the label and the chevron, and one left hidden would still be
 * holding its gap open.
 */
export function toActivityMeta(wire: string | null | undefined, tool?: string | null): string | undefined {
  if (tool && NO_META_TOOLS.has(tool)) return undefined;
  const meta = wire?.trim();
  return !meta || /^rev\s/i.test(meta) ? undefined : meta;
}
